import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Flame, Zap, ListMusic } from "lucide-react";
import PhoneMockup from "../components/PhoneMockup";
import Waveform from "../components/Waveform";
import { Reveal, MaskLine } from "../components/Reveal";

const FILTERS = ["All live", "Trending", "Genre hubs", "DJ sets", "Friends"];

const ROOMS = [
  {
    name: "Rooftop Sunset",
    host: "Maya",
    initials: "M",
    img: "https://images.unsplash.com/photo-1603910234616-3b5f4a6be2b4?q=80&w=900&auto=format&fit=crop",
    tags: ["Trending", "DJ sets"],
    now: "One More Time — Daft Punk",
  },
  {
    name: "Late Night Drive",
    host: "Dee",
    initials: "D",
    img: "https://images.unsplash.com/photo-1755545587800-d1e9b5dc4fea?q=80&w=900&auto=format&fit=crop",
    tags: ["Genre hubs", "Friends"],
    now: "Nightcall — Kavinsky",
  },
  {
    name: "Vinyl Therapy",
    host: "Vale",
    initials: "V",
    img: "https://images.unsplash.com/photo-1483412033650-1015ddeb83d1?q=80&w=900&auto=format&fit=crop",
    tags: ["Friends", "Genre hubs"],
    now: "Dreams — Fleetwood Mac",
  },
  {
    name: "Indie Sleaze Revival",
    host: "Nico",
    initials: "N",
    img: "https://images.unsplash.com/photo-1450044804117-534ccd6e6a3a?q=80&w=900&auto=format&fit=crop",
    tags: ["Trending", "DJ sets"],
    now: "Kids — MGMT",
  },
];

const CHART = [
  ["1", "One More Time", "Daft Punk", "in 24 rooms"],
  ["2", "Midnight City", "M83", "in 19 rooms"],
  ["3", "Dreams", "Fleetwood Mac", "in 16 rooms"],
  ["4", "Redbone", "Childish Gambino", "in 12 rooms"],
];

const AV_GRADIENTS = [
  "linear-gradient(135deg,#8b4dff,#4d7cff)",
  "linear-gradient(135deg,#ec4899,#8b4dff)",
  "linear-gradient(135deg,#06b6d4,#4d7cff)",
  "linear-gradient(135deg,#f59e0b,#ec4899)",
];

export default function Discover() {
  const [filter, setFilter] = useState("All live");
  const rooms = filter === "All live" ? ROOMS : ROOMS.filter((r) => r.tags.includes(filter));

  return (
    <>
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-12" data-testid="discover-hero">
        <div className="glow-blob glow-azure" style={{ width: 560, height: 560, top: "-28%", left: "-10%" }} aria-hidden="true" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
          <p className="eyebrow" data-testid="discover-eyebrow">
            <span className="live-dot" /> Discovery
          </p>
          <h1 className="hero-title mt-6 !text-[clamp(2rem,4.6vw,3.4rem)]" data-testid="discover-title">
            <MaskLine delay={0.05}>Find your next favourite —</MaskLine>
            <MaskLine delay={0.18}>
              <span className="text-gradient">together.</span>
            </MaskLine>
          </h1>
          <p className="body-lg max-w-xl mt-6">
            No algorithmic fog. Discover comes from real rooms, real crowds and the people you
            already trust with the aux.
          </p>
        </div>
      </section>

      <section className="pb-8" data-testid="discover-filters">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 flex flex-wrap gap-2.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              data-testid={`discover-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                filter === f
                  ? "bg-gradient-to-r from-[#8b4dff] to-[#4d7cff] text-white shadow-glow-sm"
                  : "glass text-dusk hover:text-chalk"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="section !pt-8" data-testid="discover-rooms">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {rooms.map((room, i) => (
            <Reveal key={room.name} delay={i * 0.06} testid={`room-card-${room.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <article className="room-card glass rounded-3xl overflow-hidden h-full flex flex-col transition-transform duration-500 hover:-translate-y-1.5">
                <div className="room-cover">
                  <img src={room.img} alt={`${room.name} room`} loading="lazy" />
                  <span className="absolute top-3 right-3 live-chip z-10">Live</span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-display text-sm">{room.name}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="avatar avatar-sm" style={{ background: AV_GRADIENTS[i % 4] }}>
                      {room.initials}
                    </span>
                    <p className="text-xs text-dusk">hosted by {room.host}</p>
                  </div>
                  <p className="text-[11px] text-dusk mt-2.5 truncate">
                    <span className="text-grape-soft font-mono">♫</span> {room.now}
                  </p>
                  <div className="h-5 mt-3">
                    <Waveform bars={22} className="h-full" testid={`room-waveform-${room.name.toLowerCase().replace(/\s+/g, "-")}`} />
                  </div>
                  <Link to="/access" data-testid={`room-tap-in-${room.name.toLowerCase().replace(/\s+/g, "-")}`} className="btn-primary mt-4 w-full !py-2.5 !text-[13px]">
                    Tap in
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <p className="mx-auto max-w-6xl px-5 sm:px-8 mt-6 text-[10px] font-mono uppercase tracking-widest text-dusk/60">
          Illustrative room preview — live rooms open at launch
        </p>
      </section>

      <section className="section !pt-4" data-testid="discover-chart">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow">Most tapped this week</p>
            <h2 className="section-title mt-4">What the rooms are playing.</h2>
            <p className="body-lg mt-4 max-w-md">
              Tracks climb the board as rooms queue them. Save any of them straight to your
              library while it plays.
            </p>
            <div className="glass rounded-3xl p-5 mt-8 flex items-center gap-4">
              <div className="mini-vinyl !w-14 !h-14" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">Nightcall — Kavinsky</p>
                <Waveform bars={20} className="h-5 mt-2" />
              </div>
              <div className="flex gap-1.5">
                {[Heart, Flame, Zap].map((Icon, i) => (
                  <span key={i} className="grid place-items-center w-8 h-8 rounded-full border border-grape/25 text-grape-soft">
                    <Icon size={13} />
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="glass rounded-3xl divide-y divide-grape/10 overflow-hidden">
              {CHART.map(([rank, title, artist, roomsCount]) => (
                <li key={rank} className="flex items-center gap-4 px-5 py-4">
                  <span className="font-display text-lg text-grape-soft w-6">{rank}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{title}</p>
                    <p className="text-xs text-dusk">{artist}</p>
                  </div>
                  <span className="text-[10px] font-mono text-dusk flex-none">{roomsCount}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[10px] font-mono uppercase tracking-widest text-dusk/60">
              Illustrative chart preview
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section !pt-0">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal>
            <div className="cta-panel rounded-[2.5rem] border border-grape/25 px-6 py-14 sm:px-16 text-center" data-testid="discover-cta">
              <h2 className="section-title">The rooms are almost open.</h2>
              <p className="body-lg mt-4">Get in early and be there for the first drop.</p>
              <Link to="/access" data-testid="discover-cta-button" className="btn-primary mt-8">
                Get early access <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
